/** RaiderMapCR - Home page with feature cards */
import React from 'react';
import '../App.css';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box
} from '@mui/material';
import {
  Map as MapIcon,
  Schedule as ScheduleIcon,
  Restaurant as FoodIcon,
  ContactPhone as DirectoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Campus Map',
      description: 'Navigate with our interactive floor plan',
      icon: <MapIcon sx={{ fontSize: 48, display: 'block', margin: 0, padding: 0 }} />,
      path: '/map',
      iconColor: '#ffffff',
      gradient: 'linear-gradient(135deg, #AA3333 0%, #e63946 100%)',
      textColor: '#ffffff'
    },
    {
      title: 'Current Courses',
      description: 'Browse all current courses',
      icon: <ScheduleIcon sx={{ fontSize: 48, display: 'block', margin: 0, padding: 0 }} />,
      path: '/schedule',
      iconColor: '#ffffff',
      gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
      textColor: '#ffffff'
    },
    {
      title: 'Directory',
      description: 'Find faculty, staff, and service information',
      icon: <DirectoryIcon sx={{ fontSize: 48, display: 'block', margin: 0, padding: 0 }} />,
      path: '/directory',
      iconColor: '#ffffff',
      gradient: 'linear-gradient(135deg, #059669 0%, #0891b2 100%)',
      textColor: '#ffffff'
    },
    {
      title: 'Food Recommendations',
      description: 'Discover dining options near campus',
      icon: <FoodIcon sx={{ fontSize: 48, display: 'block', margin: 0, padding: 0 }} />,
      path: '/food-services',
      iconColor: '#ffffff',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
      textColor: '#ffffff'
    }
  ];

  return (
    <div className="main-content">
      <Container
        maxWidth="lg"
        sx={{
          py: 3,
          pt: 5,
          px: { xs: 2, md: 4 }
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Campus Navigation Made Simple
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph sx={{ color: '#e0e0e0' }}>
            Find what you're looking for at TTU Costa Rica
          </Typography>
        </Box>

        <Box sx={{
          maxWidth: '700px',
          mx: 'auto',
          width: '100%',
          px: { xs: 1, md: 0 }
        }}>
          <Grid
            container
            spacing={2}
          >
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <div
                  className={index === 0 ? 'campus-map-glow-wrapper' : undefined}
                  style={{ height: '100%' }}
                >
                  <Card
                    onClick={() => navigate(feature.path)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      background: feature.gradient,
                      border: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '@media (hover: hover)': {
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                        }
                      },
                      '@media (hover: none)': {
                        '&:hover': {
                          transform: 'none',
                          boxShadow: 'none',
                        }
                      }
                    }}
                  >
                    <CardContent sx={{
                      flexGrow: 1,
                      textAlign: 'center',
                      py: 2.5,
                      px: 2,
                      color: feature.textColor
                    }}>
                      <Box sx={{
                        color: feature.iconColor,
                        mb: 1.5,
                        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '48px',
                        '& .MuiSvgIcon-root': {
                          margin: 0,
                          padding: 0,
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          fontSize: '48px'
                        }
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          color: feature.textColor,
                          fontWeight: 600,
                          mb: 1,
                          fontSize: '1.1rem'
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: feature.textColor,
                          opacity: 0.9,
                          lineHeight: 1.4,
                          fontSize: '0.85rem'
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </div>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            About RaiderMapCR
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', pb: 3, color: '#e0e0e0' }}>
            RaiderMapCR is designed to enhance your experience at Texas Tech University's Costa Rica campus.
            From finding classrooms to choosing your next meal, it has it all!
          </Typography>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center', pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Typography variant="body2" color="text.secondary" sx={{ color: '#b0b0b0' }}>
            contact@raidermapcr.app
          </Typography>
        </Box>
      </Container>
    </div>
  );
};

export default HomePage;
